import { EnvironmentProviders, Provider, makeEnvironmentProviders } from "@angular/core";

import { FirebaseApp, FirebaseOptions, initializeApp } from "firebase/app";
import type { AppCheck } from "firebase/app-check";
import { getAnalytics } from "firebase/analytics";
import { Auth, getAuth } from "firebase/auth";
import { Database, getDatabase } from "firebase/database";
import { DataConnect as AngularFireDataConnect } from "@angular/fire/data-connect";
import type { DataConnect as FirebaseDataConnect } from "firebase/data-connect";
import { Firestore, getFirestore } from "firebase/firestore";
import { Functions, getFunctions } from "firebase/functions";
import { getMessaging } from "firebase/messaging";
import { getPerformance } from "firebase/performance";
import { getRemoteConfig } from "firebase/remote-config";
import { FirebaseStorage, getStorage } from "firebase/storage";

import {
  FIREBASE_ANALYTICS,
  FIREBASE_APP,
  FIREBASE_APP_CHECK,
  FIREBASE_AUTH,
  FIREBASE_DATA_CONNECT,
  FIREBASE_DATABASE,
  FIREBASE_FUNCTIONS,
  FIREBASE_MESSAGING,
  FIREBASE_PERFORMANCE,
  FIREBASE_REMOTE_CONFIG,
  FIREBASE_STORAGE,
  FIRESTORE,
} from "./firebase.tokens";

type FirebaseServiceName =
  | "analytics"
  | "appCheck"
  | "auth"
  | "dataConnect"
  | "database"
  | "firestore"
  | "functions"
  | "messaging"
  | "performance"
  | "remoteConfig"
  | "storage";

export interface FirebaseServiceOptions {
  analytics?: boolean;
  /** Requires `appCheckFactory`. Register the app with App Check *before* enabling this on any
   *  app that will use Firestore/Storage/Functions — a service's App Check token is only attached
   *  to requests made after `initializeAppCheck()` has run on that app. */
  appCheck?: boolean;
  auth?: boolean;
  dataConnect?: boolean;
  database?: boolean;
  firestore?: boolean;
  functions?: boolean;
  messaging?: boolean;
  performance?: boolean;
  remoteConfig?: boolean;
  storage?: boolean;
}

export interface FirebaseEmulatorOptions {
  auth?: (auth: Auth) => void;
  dataConnect?: (dataConnect: FirebaseDataConnect) => void;
  database?: (database: Database) => void;
  firestore?: (firestore: Firestore) => void;
  functions?: (functions: Functions) => void;
  storage?: (storage: FirebaseStorage) => void;
}

export interface ProvideFirebaseOptions {
  /** Required when `services.appCheck` is true. Typically `(app) => initializeAppCheck(app, {...})`. */
  appCheckFactory?: (app: FirebaseApp) => AppCheck;
  dataConnectFactory?: (app: FirebaseApp) => FirebaseDataConnect;
  emulators?: FirebaseEmulatorOptions;
  services?: FirebaseServiceOptions;
}

export function provideFirebase(config: FirebaseOptions, options: ProvideFirebaseOptions = {}): EnvironmentProviders {
  const providers: Provider[] = [{ provide: FIREBASE_APP, useFactory: () => initializeApp(config) }];
  const services = options.services ?? {};

  if (isEnabled(services, "appCheck") && options.appCheckFactory) {
    providers.push({
      provide: FIREBASE_APP_CHECK,
      useFactory: (app: FirebaseApp) => options.appCheckFactory!(app),
      deps: [FIREBASE_APP],
    });
  }

  if (isEnabled(services, "analytics")) {
    providers.push({
      provide: FIREBASE_ANALYTICS,
      useFactory: (app: FirebaseApp) => getAnalytics(app),
      deps: [FIREBASE_APP],
    });
  }

  if (isEnabled(services, "auth")) {
    providers.push({
      provide: FIREBASE_AUTH,
      useFactory: (app: FirebaseApp) => {
        const auth = getAuth(app);
        options.emulators?.auth?.(auth);
        return auth;
      },
      deps: [FIREBASE_APP],
    });
  }

  if (isEnabled(services, "dataConnect") && options.dataConnectFactory) {
    providers.push(
      {
        provide: FIREBASE_DATA_CONNECT,
        useFactory: (app: FirebaseApp) => {
          const dataConnect = options.dataConnectFactory!(app);
          options.emulators?.dataConnect?.(dataConnect);
          return dataConnect;
        },
        deps: [FIREBASE_APP],
      },
      {
        provide: AngularFireDataConnect,
        useExisting: FIREBASE_DATA_CONNECT,
      }
    );
  }
  if (isEnabled(services, "database")) {
    providers.push({
      provide: FIREBASE_DATABASE,
      useFactory: (app: FirebaseApp) => {
        const database = getDatabase(app);
        options.emulators?.database?.(database);
        return database;
      },
      deps: [FIREBASE_APP],
    });
  }

  if (isEnabled(services, "firestore")) {
    providers.push(
      {
        provide: FIRESTORE,
        useFactory: (app: FirebaseApp) => {
          const firestore = getFirestore(app);
          options.emulators?.firestore?.(firestore);
          return firestore;
        },
        deps: [FIREBASE_APP],
      },
      {
        provide: Firestore,
        useExisting: FIRESTORE,
      }
    );
  }

  if (isEnabled(services, "functions")) {
    providers.push({
      provide: FIREBASE_FUNCTIONS,
      useFactory: (app: FirebaseApp) => {
        const functions = getFunctions(app);
        options.emulators?.functions?.(functions);
        return functions;
      },
      deps: [FIREBASE_APP],
    });
  }

  if (isEnabled(services, "messaging")) {
    providers.push({
      provide: FIREBASE_MESSAGING,
      useFactory: (app: FirebaseApp) => getMessaging(app),
      deps: [FIREBASE_APP],
    });
  }

  if (isEnabled(services, "performance")) {
    providers.push({
      provide: FIREBASE_PERFORMANCE,
      useFactory: (app: FirebaseApp) => getPerformance(app),
      deps: [FIREBASE_APP],
    });
  }

  if (isEnabled(services, "remoteConfig")) {
    providers.push({
      provide: FIREBASE_REMOTE_CONFIG,
      useFactory: (app: FirebaseApp) => getRemoteConfig(app),
      deps: [FIREBASE_APP],
    });
  }

  if (isEnabled(services, "storage")) {
    providers.push({
      provide: FIREBASE_STORAGE,
      useFactory: (app: FirebaseApp) => {
        const storage = getStorage(app);
        options.emulators?.storage?.(storage);
        return storage;
      },
      deps: [FIREBASE_APP],
    });
  }

  return makeEnvironmentProviders(providers);
}

function isEnabled(services: FirebaseServiceOptions, name: FirebaseServiceName): boolean {
  return services[name] === true;
}
